/**
 * One box on a diagram: a state, or a function or its step.
 *
 * A container - a box the view shows others inside - draws with its name in a
 * title strip, so what it holds sits visibly inside it. A leaf is a rounded box.
 * The initial state carries a filled dot, UML's own mark for it; a function's
 * `start` and `done` are drawn as SysML's marks rather than as boxes.
 */

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { Handle, NodeResizer, Position } from '@xyflow/react'
import type { NodeProps } from '@xyflow/react'
import { MONOSPACE } from '../../theme'
import { MACHINE_HEADER } from './stateDiagram'
import type { StateNode } from './stateDiagram'

export default function StateNodeView({ data, selected, draggable, isConnectable }: NodeProps<StateNode>) {
  const theme = useTheme()
  const border = selected ? theme.palette.primary.main : theme.palette.text.secondary

  if (data.pseudo) {
    // SysML's own marks: a filled disc starts an action, a ringed disc ends it.
    // No resizer - a mark has no content to make room for.
    return (
      <>
        <Handle type="target" position={Position.Left} isConnectable={isConnectable && data.pseudo === 'done'} />
        <Box
          role="img"
          aria-label={data.pseudo}
          title={data.pseudo}
          sx={{
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            borderRadius: '50%',
            border: data.pseudo === 'done' ? 3 : 0,
            borderColor: selected ? 'primary.main' : 'text.primary',
            bgcolor: 'background.paper',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Box sx={{ width: '60%', height: '60%', borderRadius: '50%', bgcolor: selected ? 'primary.main' : 'text.primary' }} />
        </Box>
        <Handle type="source" position={Position.Right} isConnectable={isConnectable && data.pseudo === 'start'} />
      </>
    )
  }

  return (
    <>
      {/* Resizing is a layout edit, so it is offered exactly where dragging is. */}
      <NodeResizer isVisible={!!selected && !!draggable} minWidth={96} minHeight={44} />
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      <Box
        role="group"
        aria-label={`${data.kindLabel} ${data.label}${data.isInitial ? ' (initial)' : ''}`}
        title={data.statement || undefined}
        sx={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border: selected ? 2 : 1,
          borderColor: border,
          borderRadius: 3,
          bgcolor: data.isMachine
            ? alpha(theme.palette.primary.main, 0.04)
            : theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            height: data.isMachine ? MACHINE_HEADER : '100%',
            borderBottom: data.isMachine ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          {data.isInitial && (
            <Box
              component="span"
              aria-hidden
              sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'text.primary', flexShrink: 0 }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {data.label}
            </Typography>
            {data.identifier !== data.label && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="div"
                sx={{ fontFamily: MONOSPACE, lineHeight: 1.2 }}
              >
                {data.identifier}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable} />
    </>
  )
}
